import {
  Badge,
  Button,
  FileInput,
  Group,
  LoadingOverlay,
  Modal,
  Pagination,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import axios from "axios";
import { useMemo, useState } from "react";
import { FileSpreadsheet } from "tabler-icons-react";
import { API } from "utiles/url";
import * as XLSX from "xlsx";

const SmeSupplierImport = ({ opened, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [importing, setImporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState("25");

  const handleFileChange = async (selectedFile) => {
    setFile(selectedFile);
    if (!selectedFile) {
      setParsedData([]);
      setCurrentPage(1);
      return;
    }

    try {
      const data = await selectedFile.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const cleanedData = jsonData.map((row) => {
        const cleanedRow = {};
        Object.keys(row).forEach((key) => {
          const trimmedKey = key.trim();
          cleanedRow[trimmedKey] = row[key];
        });
        return cleanedRow;
      });

      const transformed = cleanedData.map((row, index) => ({
        name: (row.Name || row.name || "").toString().trim(),
        phone: (row.Phone || row.phone || "").toString().trim(),
        email: (row.Email || row.email || "").toString().trim(),
        password: (row.Password || row.password || "").toString().trim(),
        _index: index,
        _valid: true,
        _errors: [],
      }));

      transformed.forEach((item) => {
        const errors = [];
        if (!item.name) errors.push("Name");
        if (!item.phone) errors.push("Phone");

        if (errors.length > 0) {
          item._valid = false;
          item._errors = errors;
        }
      });

      setParsedData(transformed);
      setCurrentPage(1);
    } catch (error) {
      showNotification({
        color: "red",
        title: "Parse Error",
        message: error.message,
      });
      setParsedData([]);
      setCurrentPage(1);
    }
  };

  const handleImport = async () => {
    if (!file) {
      showNotification({
        color: "red",
        title: "No File",
        message: "Please select an Excel file first",
      });
      return;
    }

    if (parsedData.length === 0) {
      showNotification({
        color: "red",
        title: "No Data",
        message: "Please select and parse an Excel file first",
      });
      return;
    }

    const invalidRows = parsedData.filter((item) => !item._valid);
    if (invalidRows.length > 0) {
      const errorDetails = invalidRows
        .map(
          (row) => `Row ${row._index + 1}: Missing ${row._errors.join(", ")}`,
        )
        .join("\n");

      showNotification({
        color: "red",
        title: "Validation Error",
        message: `${invalidRows.length} row(s) have missing required fields:\n${errorDetails}`,
        autoClose: 10000,
      });
      return;
    }

    setImporting(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const token =
        localStorage.getItem("auth_token") || localStorage.getItem("token");

      const response = await axios.post(
        `${API}/bulk-import/sme-suppliers`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: token ? `Bearer ${token}` : "",
          },
        },
      );

      setImporting(false);

      const isSuccess = response.data.message || response.data.summary;

      if (isSuccess) {
        const summary = response.data.summary;
        const message =
          response.data.message ||
          `Import complete. Created: ${summary?.created || 0}, Skipped: ${summary?.skipped || 0}, Failed: ${summary?.failed || 0}`;

        showNotification({
          color: summary?.failed > 0 ? "yellow" : "green",
          title: "Import Complete",
          message: message,
        });

        if (onSuccess) onSuccess();
        handleClose();
      } else {
        showNotification({
          color: "red",
          title: "Import Failed",
          message: response.data.message || "Failed to import SME suppliers",
        });
      }
    } catch (error) {
      setImporting(false);
      console.error("Import error:", error);
      showNotification({
        color: "red",
        title: "Import Failed",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "An error occurred while importing SME suppliers",
      });
    }
  };

  const handleClose = () => {
    setFile(null);
    setParsedData([]);
    setCurrentPage(1);
    setRowsPerPage("25");
    onClose();
  };

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * parseInt(rowsPerPage);
    const end = start + parseInt(rowsPerPage);
    return parsedData.slice(start, end);
  }, [parsedData, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(parsedData.length / parseInt(rowsPerPage));
  const startIndex = (currentPage - 1) * parseInt(rowsPerPage) + 1;
  const endIndex = Math.min(
    currentPage * parseInt(rowsPerPage),
    parsedData.length,
  );

  const handlePageSizeChange = (value) => {
    setRowsPerPage(value);
    setCurrentPage(1);
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Import SME Suppliers"
      size="70%"
      style={{ marginTop: "100px" }}
      overlayProps={{ opacity: 0.55, blur: 3 }}
    >
      <LoadingOverlay visible={importing} />
      <Stack spacing="md">
        <FileInput
          label="Select Excel File"
          placeholder="Choose .xlsx or .xls file"
          icon={<FileSpreadsheet size={14} />}
          value={file}
          onChange={handleFileChange}
          accept=".xlsx,.xls"
          clearable
        />

        {parsedData.length > 0 && (
          <>
            <Group position="apart" align="center">
              <Text size="sm" weight={500}>
                Found {parsedData.length} row(s)
              </Text>
              {parsedData.filter((item) => !item._valid).length > 0 && (
                <Group spacing="xs">
                  <Badge color="red" size="lg">
                    {parsedData.filter((item) => !item._valid).length} invalid
                    row(s)
                  </Badge>
                  <Text size="sm" color="red" weight={600}>
                    Rows:{" "}
                    {parsedData
                      .filter((item) => !item._valid)
                      .map((item) => item._index + 1)
                      .join(", ")}
                  </Text>
                </Group>
              )}
            </Group>

            <ScrollArea style={{ height: 450 }}>
              <Table striped highlightOnHover fontSize="xs">
                <thead style={{ backgroundColor: "#FF6A00" }}>
                  <tr>
                    <th style={{ color: "white" }}>#</th>
                    <th style={{ color: "white" }}>Name</th>
                    <th style={{ color: "white" }}>Phone</th>
                    <th style={{ color: "white" }}>Email</th>
                    <th style={{ color: "white" }}>Password</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((item) => (
                    <tr
                      key={item._index}
                      style={{
                        backgroundColor: !item._valid
                          ? "#ffe6e6"
                          : "transparent",
                      }}
                    >
                      <td>{item._index + 1}</td>
                      <td
                        style={{
                          backgroundColor:
                            !item._valid && item._errors.includes("Name")
                              ? "#ffcccc"
                              : "transparent",
                        }}
                      >
                        {item.name || (
                          <Text size="xs" color="red">
                            Missing
                          </Text>
                        )}
                      </td>
                      <td
                        style={{
                          backgroundColor:
                            !item._valid && item._errors.includes("Phone")
                              ? "#ffcccc"
                              : "transparent",
                        }}
                      >
                        {item.phone || (
                          <Text size="xs" color="red">
                            Missing
                          </Text>
                        )}
                      </td>
                      <td>{item.email}</td>
                      <td>
                        {item.password ? (
                          "*".repeat(item.password.length)
                        ) : (
                          <Text size="xs" color="dimmed">
                            Optional
                          </Text>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </ScrollArea>

            <Group position="apart" align="center" mt="md">
              <Group spacing="xs" align="center">
                <Text size="sm" color="dimmed">
                  Rows per page:
                </Text>
                <Select
                  value={rowsPerPage}
                  onChange={handlePageSizeChange}
                  data={[
                    { value: "10", label: "10" },
                    { value: "25", label: "25" },
                    { value: "50", label: "50" },
                    { value: "100", label: "100" },
                  ]}
                  size="xs"
                  style={{ width: 80 }}
                />
                <Text size="sm" color="dimmed">
                  {startIndex}–{endIndex} of {parsedData.length}
                </Text>
              </Group>
              <Pagination
                page={currentPage}
                onChange={setCurrentPage}
                total={totalPages}
                size="sm"
                withEdges
              />
            </Group>
          </>
        )}

        <Group position="right" mt="md">
          <Button variant="subtle" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={parsedData.length === 0 || importing}
            loading={importing}
            style={{
              backgroundColor: "#FF6A00",
              color: "#FFFFFF",
            }}
          >
            Submit {parsedData.length > 0 && `(${parsedData.length})`}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default SmeSupplierImport;
